import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

/**
 * Region type: "IN" | "EU" | "OTHER" | "UNKNOWN"
 */

/**
 * Client-side best-effort detector (fallback)
 */
function detectRegionClient() {
  try {
    const tz = (Intl.DateTimeFormat().resolvedOptions().timeZone || "").toLowerCase();
    if (tz.includes("kolkata") || tz.includes("india") || tz.includes("asia/kolkata")) return "IN";
    if (tz.startsWith("europe/")) return "EU";

    // languages
    const langs = navigator.languages || [navigator.language];
    for (const L of langs) {
      if (!L) continue;
      const parts = String(L).toUpperCase().split("-");
      if (parts[1] === "IN") return "IN";
      if (
        parts[1] &&
        [
          "AL", "AD", "AM", "AT", "AZ", "BY", "BE", "BA", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR",
          "GE", "DE", "GR", "HU", "IS", "IE", "IT", "KZ", "XK", "LV", "LI", "LT", "LU", "MT", "MD", "MC",
          "ME", "NL", "MK", "NO", "PL", "PT", "RO", "RU", "SM", "RS", "SK", "SI", "ES", "SE", "CH", "TR",
          "UA", "GB"
        ].includes(parts[1])
      ) {
        return "EU";
      }
    }

    // timezone keywords
    const tzKeywords = ["paris", "rome", "london", "berlin", "madrid", "amsterdam", "stockholm", "vienna", "prague", "lisbon"];
    if (tzKeywords.some(k => tz.includes(k))) return "EU";

    return "OTHER";
  } catch (e) {
    return "UNKNOWN";
  }
}

/**
 * Map ISO country code to Region
 */
function countryCodeToRegion(code) {
  if (!code) return "UNKNOWN";
  const c = String(code).toUpperCase();
  if (c === "IN") return "IN";

  const euList = new Set([
    "AL", "AD", "AM", "AT", "AZ", "BY", "BE", "BA", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR",
    "GE", "DE", "GR", "HU", "IS", "IE", "IT", "KZ", "XK", "LV", "LI", "LT", "LU", "MT", "MD", "MC",
    "ME", "NL", "MK", "NO", "PL", "PT", "RO", "RU", "SM", "RS", "SK", "SI", "ES", "SE", "CH", "TR",
    "UA", "GB"
  ]);

  if (euList.has(c)) return "EU";
  return "OTHER";
}

/**
 * Reverse geocode lat/lon to country with public free endpoint (no API key).
 * Uses BigDataCloud reverse-geocode-client.
 */
async function reverseGeocodeCountry(lat, lon, timeoutMs = 5000) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const url = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=en`;
    const resp = await fetch(url, { signal: controller.signal });
    clearTimeout(id);

    if (!resp.ok) return null;
    const json = await resp.json();
    return json?.countryCode ?? json?.country_code ?? null;
  } catch (e) {
    clearTimeout(id);
    return null;
  }
}

/**
 * Try browser geolocation API (asks permission). Returns Region or null if unavailable/denied.
 */
async function detectRegionBrowser() {
  if (!("geolocation" in navigator)) return null;

  const getPos = () =>
    new Promise((resolve, reject) => {
      let resolved = false;
      const onSuccess = (pos) => {
        if (!resolved) {
          resolved = true;
          resolve(pos);
        }
      };
      const onError = (err) => {
        if (!resolved) {
          resolved = true;
          reject(err);
        }
      };

      navigator.geolocation.getCurrentPosition(onSuccess, onError, {
        maximumAge: 60000,
        timeout: 8000,
        enableHighAccuracy: false
      });

      setTimeout(() => {
        if (!resolved) {
          resolved = true;
          reject(new Error("geolocation timeout"));
        }
      }, 9000);
    });

  try {
    const pos = await getPos();
    const lat = pos.coords.latitude;
    const lon = pos.coords.longitude;
    const country = await reverseGeocodeCountry(lat, lon, 5000);
    if (!country) return null;
    return countryCodeToRegion(country);
  } catch (e) {
    return null;
  }
}

// EUR price conversion (approximate: 499 INR ≈ 5.5 EUR, rounded to 6 EUR)
const EUR_PRICE_FOR_499 = 6.0;
// EUR price for 1 month plan
const EUR_PRICE_FOR_1M = 5.0;

/** -------------------- CONTENT (copy/data only) -------------------- */
const CONTENT = {
  jobsuite: {
    headerTitle: "Choose Your Plan",
    headerSub: "Select the perfect plan to accelerate your career journey with our AI-powered tools",
    businessName: "Rakfort Career Services",
    plans: [
      {
        name: "Free Plan",
        price: 0,
        months: 0,
        features: [
          "Map Analysis",
          "Simple Cover Letter Generator (5/month)",
          "5 AI Interview Questions/month",
          "Standard Resume Review",
          "Email Support (48h response)",
          "Limited Usage"
        ],
        recommended: false,
        cta: "Get Started"
      },
      {
        name: "Premium — 1 month",
        price: 499,
        months: 1,
        features: [
          "AI-Powered Cover Letters",
          "AI Interview Questions",
          "Career Copilot",
          "Interview Copilot",
          "Priority Support",
          "Full AI Suite Access"
        ],
        recommended: false,
        cta: "Subscribe Now"
      },
      {
        name: "Premium — 3 months",
        price: 1199,
        months: 3,
        features: [
          "AI-Powered Cover Letters",
          "AI Interview Questions",
          "Career Copilot",
          "Interview Copilot",
          "Mock Interview",
          "Priority 24/7 Support",
          "Personalized Job Matches",
          "Career Path Planning",
          "Unlimited Usage"
        ],
        recommended: true,
        cta: "Subscribe Now"
      },
      {
        name: "Premium — 6 months",
        price: 1999,
        months: 6,
        features: [
          "AI-Powered Cover Letters",
          "AI Interview Questions",
          "Career Copilot",
          "Interview Copilot",
          "Mock Interview",
          "Priority 24/7 Support",
          "Personalized Job Matches",
          "Career Path Planning",
          "Unlimited Usage",
          "Best Value Savings"
        ],
        recommended: false,
        cta: "Subscribe Now"
      }
    ],
  },

  studysuite: {
    headerTitle: "Choose Your Plan",
    headerSub: "Pick a plan to power your study-abroad journey with AI guidance and document tools",
    businessName: "Rakfort Study Suite",
    plans: [
      {
        name: "Starter Plan",
        price: 0,
        months: 0,
        features: [
          "AI Course Suggestions (5/month)",
          "Visa/University Mock Q&A (5/month)",
          "Basic SOP/LOR/CV Templates",
          "Email Support (48h response)",
          "Limited Usage"
        ],
        recommended: false,
        cta: "Get Started"
      },
      {
        name: "Pro — 1 month",
        price: 499,
        months: 1,
        features: [
          "AI Course & University Suggestions",
          "Visa & University Mock Interviews",
          "SOP / LOR / CV / Cover Builder",
          "Originality & Word Checks",
          "Priority Support",
          "Full Suite Access"
        ],
        recommended: false,
        cta: "Subscribe Now"
      },
      {
        name: "Pro — 3 months",
        price: 1199,
        months: 3,
        features: [
          "Unlimited AI Course & University Suggestions",
          "Unlimited Visa & University Mock Interviews",
          "SOP / LOR / CV / Cover Builder",
          "Originality, tone & word-limit checks",
          "Scholarship & Intake Guidance",
          "Priority 24/7 Support",
          "Access to Expert Webinars",
          "Unlimited Usage"
        ],
        recommended: true,
        cta: "Subscribe Now"
      },
      {
        name: "Pro — 6 months",
        price: 1999,
        months: 6,
        features: [
          "Unlimited AI Course & University Suggestions",
          "Unlimited Visa & University Mock Interviews",
          "SOP / LOR / CV / Cover Builder",
          "Originality, tone & word-limit checks",
          "Scholarship & Intake Guidance",
          "Priority 24/7 Support",
          "Access to Expert Webinars",
          "Unlimited Usage",
          "Best Value Savings"
        ],
        recommended: false,
        cta: "Subscribe Now"
      }
    ],
  }
};

/**
 * SignupPricing
 * @param {"jobsuite"|"studysuite"} suite - content variant (defaults to "jobsuite")
 */
const SignupPricing = ({ suite = "jobsuite" }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [razorpayLoading, setRazorpayLoading] = useState(true);
  const [razorpayReady, setRazorpayReady] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [region, setRegion] = useState("UNKNOWN");
  const [detecting, setDetecting] = useState(true);
  const [manualCurrency, setManualCurrency] = useState(null); // "INR" | "EUR" | null

  // Resolve content set (fallback to jobsuite if unknown)
  const S = CONTENT[suite] || CONTENT.jobsuite;

  // Determine effective region/currency (manual selection takes precedence)
  const effectiveRegion = manualCurrency === "EUR" ? "EU" : manualCurrency === "INR" ? "IN" : region;
  const effectiveCurrency = manualCurrency || (region === "EU" ? "EUR" : "INR");

  // Filter plans based on effective region
  const getFilteredPlans = () => {
    if (effectiveRegion === "EU") {
      // For EU: only Free and 1 month plan
      const basePlan = S.plans[1]; // The 1-month plan we just added
      return [
        S.plans[0], // Free plan
        {
          ...basePlan,
          price: 1, // Non-zero trigger for logic (EUR price calculated via getPriceEur)
          recommended: true,
        }
      ];
    }
    // For others: Free, 1 month, 3 months, 6 months
    return S.plans;
  };

  const PLANS = getFilteredPlans();

  // Price helpers
  const getPriceInr = (price) => price;
  const getPriceEur = (price, months = 0) => {
    if (price === 0) return 0;
    if (months === 1) return EUR_PRICE_FOR_1M;
    // Convert INR to EUR (approximately 6 EUR per 499 INR)
    return Math.round((price / 499) * EUR_PRICE_FOR_499 * 10) / 10;
  };

  // Detection flow: 1) Browser geolocation, 2) Server endpoint, 3) Client fallback
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        setDetecting(true);

        // 1) Browser geolocation (asks user permission)
        const browserRegion = await detectRegionBrowser();
        if (browserRegion && mounted) {
          setRegion(browserRegion);
          setDetecting(false);
          return;
        }

        // 2) Server-side geo endpoint (if available)
        try {
          const res = await axios.get(`${API_BASE_URL}/geo/whoami`).catch(() => null);
          if (res && res.data) {
            const srvCode = (res.data?.country || res.data?.region || res.data?.countryCode || "").toString().toUpperCase();
            const mapped = countryCodeToRegion(srvCode || null);
            if (mounted && mapped !== "UNKNOWN") {
              setRegion(mapped);
              setDetecting(false);
              return;
            }
          }
        } catch (e) {
          // server failed — continue to fallback
        }

        // 3) client heuristic fallback
        if (mounted) {
          setRegion(detectRegionClient());
          setDetecting(false);
        }
      } catch (err) {
        if (mounted) {
          setRegion(detectRegionClient());
          setDetecting(false);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const checkRazorpay = () => {
      if (window.Razorpay) {
        setRazorpayReady(true);
        setRazorpayLoading(false);
      } else if (document.querySelector('script[src*="razorpay"]')) {
        setTimeout(checkRazorpay, 100);
      } else {
        console.error('Razorpay script not found');
        setRazorpayLoading(false);
        setError('Payment system is unavailable. Please refresh the page.');
      }
    };
    checkRazorpay();
  }, []);

  useEffect(() => {
    const auth = localStorage.getItem('user_token');
    setIsAuthenticated(!!auth);
  }, []);

  const handlePlanSelection = (plan) => {
    // Redirect directly to the job auth page as requested
    window.location.href = "https://job.novistep.com/auth";
  };

  const handlePayment = async (plan) => {
    try {
      if (effectiveCurrency === "EUR") {
        setError('EUR payments are currently being set up. Please contact support for European payments.');
        return;
      }

      if (!razorpayReady) {
        setError('Payment system is not ready. Please refresh the page.');
        return;
      }

      setLoading(true);
      setError(null);

      // Determine currency and amount based on effective currency
      const currency = effectiveCurrency;
      const amount = effectiveCurrency === "EUR"
        ? Math.round(getPriceEur(plan.price, plan.months) * 100) // cents
        : plan.price * 100; // paise

      // Create order
      const orderResponse = await axios.post(`${API_BASE_URL}/payments/create-order`, {
        amount: amount,
        currency: currency,
        receipt: `plan_${suite}_${plan.name.toLowerCase().replace(/\s+/g, '_')}_${Date.now()}`,
        notes: {
          plan_name: plan.name,
          suite,
          region: effectiveRegion,
          currency: effectiveCurrency,
          manual_currency: manualCurrency,
          months: plan.months || 0
        }
      });
      const { order } = orderResponse.data.data;
      const key_id = orderResponse?.data?.data?.key_id || process.env.REACT_APP_RAZORPAY_KEY_ID;

      // Initialize Razorpay
      const options = {
        key: key_id,
        amount: order.amount,
        currency: order.currency,
        name: S.businessName, // content-only change
        description: `${plan.name} Subscription`,
        order_id: order.id,
        handler: async (response) => {
          try {
            await axios.post(`${API_BASE_URL}/payments/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });

            alert('Payment successful! Welcome to ' + plan.name);
            navigate('/signuppricing');
          } catch (err) {
            console.error('Payment verification failed:', err);
            setError('Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          name: '',
          email: '',
          contact: ''
        },
        theme: {
          color: '#3C0F6C'
        },
        modal: {
          ondismiss: () => {
            setError('Payment cancelled.');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', function (response) {
        console.error('Payment failed:', response?.error);
        setError(response?.error?.description || 'Payment failed. Please try again.');
      });
      razorpay.open();
    } catch (err) {
      console.error('Payment initiation failed:', err);
      setError('Failed to initiate payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1A0637] via-[#2C0B4F] to-[#1A0637] text-white py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Loading State */}
        {razorpayLoading && (
          <div className="absolute top-4 right-4 bg-yellow-500/20 text-yellow-300 px-4 py-2 rounded-lg flex items-center">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Initializing payment system...
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-white to-[#FFD600]">
            {S.headerTitle}
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            {S.headerSub}
          </p>
          {/* Currency Selection */}
          <div className="flex flex-col items-center gap-4 mt-6">
            <p className="text-sm text-gray-400">
              {detecting ? "Detecting location..." : `Detected: ${region} — Using: ${effectiveCurrency === "EUR" ? "EUR (€)" : "INR (₹)"}`}
            </p>
          </div>
        </div>

        {/* Plans Grid - Adjusted to grid-cols-4 for the new plan */}
        <div className={`grid gap-6 max-w-7xl mx-auto ${effectiveRegion === "EU" ? "md:grid-cols-2 max-w-4xl" : "md:grid-cols-2 lg:grid-cols-4"}`}>
          {PLANS.map((plan) => (
            <div
              key={`${suite}-${plan.name}`}
              className={`relative bg-gradient-to-b ${plan.recommended
                ? 'from-[#3C0F6C] to-[#2C0B4F] border-2 border-[#FFD600] shadow-2xl shadow-[#FFD600]/20 hover:shadow-[#FFD600]/30'
                : 'from-white/5 to-white/[0.03] border border-white/10 hover:border-white/20 hover:shadow-lg hover:shadow-white/5'
                } backdrop-blur-lg rounded-2xl p-6 transition-all duration-300 ease-in-out transform hover:scale-[1.02] cursor-pointer flex flex-col`}
            >
              {plan.recommended && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 transition-transform duration-300">
                  <span className="bg-gradient-to-r from-[#FFD600] to-[#FFA800] text-[#3C0F6C] px-4 py-1 rounded-full text-xs font-bold shadow-lg shadow-[#FFD600]/30 flex items-center whitespace-nowrap">
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    MOST POPULAR
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-xl font-bold mb-2 transition-colors duration-300">
                  {plan.name}
                </h3>
                <div className="text-3xl font-bold mb-2">
                  <span className="">
                    {plan.price === 0
                      ? 'Free'
                      : effectiveCurrency === "EUR"
                        ? `€${getPriceEur(plan.price, plan.months).toFixed(2)}`
                        : `₹${getPriceInr(plan.price)}`
                    }
                  </span>
                  {plan.price > 0 && plan.months > 0 && (
                    <span className="text-sm text-gray-300">
                      {plan.months === 1 ? ' for 1 mo' : ` for ${plan.months} mos`}
                    </span>
                  )}
                </div>
                {plan.price === 0 && (
                  <p className="text-xs text-gray-300 mb-2">No credit card required</p>
                )}
                {plan.price > 0 && plan.months > 0 && (
                  <p className="text-xs text-gray-300 mb-2">
                    {effectiveCurrency === "EUR" ? 'Monthly subscription' : `One-time payment`}
                  </p>
                )}
              </div>

              <ul className="space-y-3 mb-8 flex-grow">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start text-sm">
                    <svg
                      className={`h-4 w-4 mt-0.5 ${plan.recommended ? 'text-[#FFD600]' : 'text-white/60'} mr-2 shrink-0`}
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2.5"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-200">
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handlePlanSelection(plan)}
                disabled={loading}
                className={`w-full py-3 px-4 rounded-xl text-center font-semibold text-sm transition-all duration-300
                  ${plan.recommended
                    ? 'bg-gradient-to-r from-[#FFD600] to-[#FFA800] text-[#3C0F6F] hover:shadow-lg hover:shadow-[#FFD600]/30 transform hover:-translate-y-1'
                    : 'bg-white/10 border border-white/20 hover:bg-white/20 hover:shadow-lg transform hover:-translate-y-1'
                  } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {loading
                  ? 'Processing...'
                  : (plan.cta || (plan.price > 0 ? 'Subscribe Now' : 'Get Started'))
                }
              </button>
            </div>
          ))}
        </div>

        {/* Error toast */}
        {error && (
          <div className="mt-8 max-w-md mx-auto bg-red-500/10 border border-red-500/30 text-red-200 p-4 rounded-lg text-center">
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default SignupPricing;